import { Alert } from '@crown/ui/alert';
import { buttonClasses } from '@crown/ui/button';
import { EXAMPLES, type Outcome, type PlaygroundModel } from './dsl-playground-model';

function Expression({ model, outcome }: { readonly model: PlaygroundModel; readonly outcome: Extract<Outcome, { kind: 'expression' }> }) @{
  <>
    <div class="dsl-result-heading">
      <h2>Generated Crown DSL</h2>
      <span class={outcome.valid ? 'dsl-badge dsl-badge--valid' : 'dsl-badge dsl-badge--invalid'}>{outcome.valid ? 'Valid Crown DSL' : 'Invalid — did not parse'}</span>
      @for (const chip of outcome.chips; key chip) {
        <span class="dsl-badge dsl-badge--muted">{chip}</span>
      }
    </div>
    <pre class="docs-code-block dsl-result-code"><code>{outcome.dsl}</code></pre>
    <div class="docs-button-row">
      <button type="button" class={buttonClasses({ variant: 'outline' })} onClick={() => void model.copy(outcome.dsl)}>{model.copied.get() ? 'Copied' : 'Copy expression'}</button>
    </div>
    @if (!outcome.valid && outcome.diagnostics) {
      <>{Alert({ variant: 'danger', title: 'Checker diagnostics', message: outcome.diagnostics ?? '' })}</>
    }
    <p class="dsl-footnote">{outcome.footnote}</p>
  </>
}

export default function DslPlayground({ model }: { readonly model: PlaygroundModel }) @{
  let field: HTMLTextAreaElement | null = null;
  const run = async (): Promise<void> => {
    if (!(await model.run())) field?.focus();
  };
  <div class="docs-page">
    <section class="docs-hero">
      <span class="docs-eyebrow">Interactive</span>
      <h1>Crown DSL Playground</h1>
      <p class="docs-hero-copy">Describe a rule, computed field or transform in plain English and the production Crown DSL model — running entirely in your browser — will author the matching expression, then validate it against the real Crown DSL grammar.</p>
    </section>
    <div class="docs-code-block mt-0">
      {Alert({
        variant: 'info',
        title: 'Runs locally in your browser — no server',
        message: 'The production crown-dsl model (an INT8 build, ~44 MB) is downloaded once, cached for offline reuse, and run entirely in your browser with onnxruntime-web. Every expression is validated by the real Crown DSL checker, also bundled client-side. Nothing you type ever leaves your device. A server fallback (the ai-service harness) is available for browsers that cannot run WebAssembly.',
      })}
    </div>
    <section class="docs-section dsl-playground" aria-labelledby="dsl-description-label">
      <label id="dsl-description-label" class="dsl-field-label" for="dsl-description">Describe what you need in plain English</label>
      <textarea
        id="dsl-description"
        class="dsl-textarea"
        rows="3"
        placeholder="e.g. patients must be between 18 and 65"
        aria-describedby="dsl-description-hint"
        ref={(element: Element) => { field = element as HTMLTextAreaElement; }}
        prop:value={() => model.description.get()}
        onInput={(event: Event) => model.description.set((event.currentTarget as HTMLTextAreaElement).value)}
        onKeydown={(event: KeyboardEvent) => {
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault();
            void run();
          }
        }}
      ></textarea>
      <p id="dsl-description-hint" class="dsl-footnote">Press Ctrl+Enter (⌘+Enter on a Mac) to generate.</p>
      <div class="dsl-example-row">
        <span class="dsl-example-label">Try:</span>
        @for (const example of EXAMPLES; key example) {
          <button type="button" class="dsl-chip" onClick={() => { model.description.set(example); field?.focus(); }}>{example}</button>
        }
      </div>
      <div class="dsl-options">
        <span class="dsl-option">
          <input type="checkbox" id="dsl-local" prop:checked={() => model.local.get()} onChange={(event: Event) => model.local.set((event.currentTarget as HTMLInputElement).checked)} />
          <label for="dsl-local">Run the AI in my browser (local, no server)</label>
        </span>
        <span class="dsl-option">
          <input type="checkbox" id="dsl-repair" prop:checked={() => model.repair.get()} onChange={(event: Event) => model.repair.set((event.currentTarget as HTMLInputElement).checked)} />
          <label for="dsl-repair">Self-repair invalid drafts</label>
        </span>
      </div>
      <div class="docs-button-row dsl-actions">
        <button type="button" class={buttonClasses({ variant: 'primary' })} disabled={() => model.busy.get()} onClick={() => void run()}>{model.busy.get() ? 'Generating…' : 'Generate DSL'}</button>
      </div>
      @if (model.progress.get()) {
        <div class="dsl-progress" role="progressbar" aria-label={model.progress.get()?.text ?? ''} aria-valuemin={0} aria-valuemax={100} aria-valuenow={model.progress.get()?.ratio == null ? undefined : Math.round((model.progress.get()?.ratio ?? 0) * 100)}>
          <span class="dsl-progress-text">{model.progress.get()?.text ?? ''}</span>
          <div class="dsl-progress-track">
            <div class={model.progress.get()?.ratio == null ? 'dsl-progress-bar dsl-progress-bar--indeterminate' : 'dsl-progress-bar'} style={`width: ${model.progress.get()?.ratio == null ? 100 : Math.round((model.progress.get()?.ratio ?? 0) * 100)}%`}></div>
          </div>
        </div>
      }
      <div class="dsl-status" role="status">{model.status.get()}</div>
      <div class="dsl-output" aria-live="polite">
        @if (model.outcome.get()?.kind === 'expression') {
          <Expression model={model} outcome={model.outcome.get() as Extract<Outcome, { kind: 'expression' }>} />
        } @else if (model.outcome.get()?.kind === 'notice') {
          <>{(() => {
            const notice = model.outcome.get() as Extract<Outcome, { kind: 'notice' }>;
            return Alert({ variant: notice.variant, title: notice.title, message: notice.message });
          })()}</>
        }
      </div>
    </section>
  </div>
}
